// Sliding session: renew the login token while the user is active.
//
// Tokens carry a fixed lifetime from sign-in (12h for email login), so someone
// using the site all day used to be signed out mid-task. Now, on user activity,
// once the token is past half its life we swap it for a fresh one via
// /api/refresh-token. Idle sessions still expire on schedule, and the server
// caps the whole session at SESSION_MAX_DAYS after the real sign-in.

import axios from "axios";
import { adoptRefreshedToken } from "./socketClient";

const CHECK_INTERVAL_MS = 60 * 1000; // look at most once a minute
const ACTIVITY_EVENTS = ["pointerdown", "keydown", "scroll", "touchstart"];

let lastCheck = 0;
let inFlight = false;

function readClaims(token) {
  try {
    const b64 = token.split(".")[1].replace(/-/g, "+").replace(/_/g, "/");
    return JSON.parse(atob(b64));
  } catch (_) {
    return null;
  }
}

async function maybeRefresh() {
  const now = Date.now();
  if (inFlight || now - lastCheck < CHECK_INTERVAL_MS) return;
  lastCheck = now;

  const token = localStorage.getItem("auth-token");
  const claims = token && readClaims(token);
  if (!claims || !claims.exp || !claims.iat || claims.impersonated) return;

  const nowSec = now / 1000;
  const remaining = claims.exp - nowSec;
  // Expired tokens are left to the normal session-expired flow.
  if (remaining <= 0 || remaining > (claims.exp - claims.iat) / 2) return;

  inFlight = true;
  try {
    const { data } = await axios.post("/api/refresh-token", {});
    // Only store it if nobody signed out / in as someone else meanwhile.
    if (data && data.refreshed && data.token && localStorage.getItem("auth-token") === token) {
      localStorage.setItem("auth-token", data.token);
      adoptRefreshedToken(data.token);
    }
  } catch (_) {
    // Not fatal: the current token stays valid until it expires.
  } finally {
    inFlight = false;
  }
}

let started = false;

export function startSessionRefresh() {
  if (started) return;
  started = true;
  ACTIVITY_EVENTS.forEach((e) =>
    window.addEventListener(e, maybeRefresh, { passive: true, capture: true })
  );
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "visible") maybeRefresh();
  });
}
